import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import {
  MODE_TYPES,
  PATTERNS,
  PLANTING_STATUS,
} from "configurations/Constants";
import DatePicker from "components/common/DatePicker";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import PlantedAcresService from "services/PlantedAcresService";
import BackDropLoader from "components/common/BackDropLoader";
import Lottie from "react-lottie";
import { PLANTED_ACRES_LOTTIE_OPTIONS } from "configurations/Constants";
import { format } from "date-fns";
import {
  setSavedPlantedAcre,
  setSavePlantedAcreSuccess,
  setSelectedPlantedAcre,
  setPlantedAcres,
} from "reduxStore/slices/plantedAcresSlice";
import { setPlantedAcreMode } from "reduxStore/slices/plantedAcresSlice";
import {
  PLANTED_ACRES_SAVE_ERROR,
  PLEASE_ENTER_PLANTED_ACRES_NAME,
  PLEASE_SELECT_STATUS,
  PLEASE_SELECT_CROP,
  PLEASE_SELECT_SEED,
  PLEASE_SELECT_TILLAGE,
  PLEASE_ENTER_ACRES,
  PLEASE_SELECT_DATE,
  PLEASE_ENTER_SEEDING_AMOUNT,
  PLEASE_ENTER_ROW_SPACING,
  PLEASE_SELECT_PATTERN,
  LABEL_SELECT_FAILED_ACRES,
  LABEL_SELECT_CROP,
  LABEL_SELECT_SEED,
  LABEL_SELECT_TILLAGE,
  LABEL_SELECT_PATTERN,
  PLEASE_ENTER_FAILED_ACRES,
  LABEL_ENTER_FAILED_ACRES,
  LABEL_ENTER_PLANTED_ACRES_NAME,
  LABEL_ENTER_ACRES,
  LABEL_ENTER_SEEDING_AMOUNT,
  LABEL_ENTER_ROW_SPACING,
  FAILED_ACRES_CANNOT_EXCEED_ACRES,
  PLANTED_ACRES_FETCH_ERROR,
  PLANTED_ACRES_NAME_ALREADY_EXISTS,
  LABEL_REPLANTING_FAILED_ACRES,
  FAILED_FROM_NOTE,
  REPLANTING_FOR_NOTE,
  ACRES_MUST_BE_GREATER_THAN_ZERO,
  ADD_PLANTED_ACRES,
  VIEW_PLANTED_ACRES,
  EDIT_PLANTED_ACRES,
} from "configurations/MessageConstants";
import { toast } from "react-toastify";
import { InputHelper } from "utils/InputHelper";

const PlantedAcresForm = ({ mode, show, onClose }) => {
  const dispatch = useDispatch();
  const [replanting, setReplanting] = useState(false);
  const [failedAcresList, setFailedAcresList] = useState([]);
  const editedPlantedAcre = useSelector(
    (state) => state.plantedAcres.editedPlantedAcre
  );

  const plantedAcres = useSelector((state) => state.plantedAcres.plantedAcres);
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const crops = useSelector((state) => state.site.crops);
  const tillages = useSelector((state) => state.site.tillages);
  const seeds = useSelector((state) => state.site.seeds);
  const isPlanned = editedPlantedAcre?.status === PLANTING_STATUS.PLANNED;
  const isActive = editedPlantedAcre?.status === PLANTING_STATUS.ACTIVE;
  const isFailed = editedPlantedAcre?.status === PLANTING_STATUS.FAILED;
  const isHarvested = editedPlantedAcre?.status === PLANTING_STATUS.HARVESTED;
  const isSharedViewOnly = selectedSite?.isShared && !selectedSite?.editor;

  const defaultValues = {
    failedCropId: null,
    status: PLANTING_STATUS.PLANNED,
    name: "",
    acres: "",
    eventDate: null,
    cropName: "",
    seedName: "",
    tillageName: "",
    seedingRate: "pounds",
    seedingAmount: "",
    rowSpacing: "",
    pattern: "",
    failedAcres: null,
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({ defaultValues });
  const status = watch("status");
  const acres = watch("acres");
  const failedCropId = watch("failedCropId");

  useEffect(() => {
    if (show && mode === MODE_TYPES.EDIT && editedPlantedAcre) {
      reset({
        failedCropId: editedPlantedAcre.failedCropId,
        status: editedPlantedAcre.status,
        name: editedPlantedAcre.name,
        acres: editedPlantedAcre.acres,
        eventDate: editedPlantedAcre.eventDate,
        cropName: editedPlantedAcre.cropName,
        seedName: editedPlantedAcre.seedName,
        tillageName: editedPlantedAcre.tillageName,
        seedingRate: editedPlantedAcre.seedingRate,
        seedingAmount: editedPlantedAcre.seedingAmount,
        rowSpacing: editedPlantedAcre.rowSpacing,
        pattern: editedPlantedAcre.pattern,
        failedAcres: editedPlantedAcre.failedAcres,
      });
    }
    dispatch(setPlantedAcreMode(mode));
  }, [show, mode, editedPlantedAcre, dispatch, reset]);

  const getFailedAcresBySiteId = async () => {
    try {
      let params = `year=${2025}&status=Failed`;
      if (selectedSite && selectedSite.id) {
        const response = await PlantedAcresService.getPlantedAcresBySiteId(
          selectedSite.id,
          params
        );
        setFailedAcresList(response.items);
      }
    } catch (error) {
      console.error(PLANTED_ACRES_FETCH_ERROR, error);
    }
  };

  useEffect(() => {
    if (replanting) {
      getFailedAcresBySiteId();
    }
  }, [replanting]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        id: editedPlantedAcre?.id,
        failedCropId: data.failedCropId,
        status: data.status,
        name: data.name,
        acres: data.acres,
        eventDate: format(data.eventDate, "MM/dd/yyyy"),
        cropName: data.cropName,
        seedName: data.seedName,
        tillageName: data.tillageName,
        seedingRate: data.seedingRate,
        seedingAmount: data.seedingAmount,
        rowSpacing: data.rowSpacing,
        pattern: data.pattern,
        failedAcres: data.failedAcres,
      };
      const response =
        mode === MODE_TYPES.ADD
          ? await PlantedAcresService.addPlantedAcres(selectedSite.id, payload)
          : await PlantedAcresService.updatePlantedAcres(
              selectedSite.id,
              editedPlantedAcre.id,
              payload
            );
      handleSave(response);
      const updatedPlantedAcres =
        mode === MODE_TYPES.ADD
          ? [response, ...plantedAcres]
          : response.parentCrop && response.failedCrop
          ? [
              response.failedCrop,
              ...plantedAcres.map((plantedAcre) =>
                plantedAcre.id === response.parentCrop.id
                  ? response.parentCrop
                  : plantedAcre
              ),
            ]
          : plantedAcres.map((plantedAcre) =>
              plantedAcre.id === response.id ? response : plantedAcre
            );
      dispatch(setPlantedAcres(updatedPlantedAcres));
      if (response.parentCrop && response.failedCrop)
        handleSave(response.parentCrop);
      else handleSave(response);
      handleClose();
    } catch (error) {
      console.error(PLANTED_ACRES_SAVE_ERROR, error);
      toast.error(error.response.data.detail);
    }
  };

  const handleSave = (plantedAcre) => {
    dispatch(setSavedPlantedAcre(plantedAcre));
    dispatch(setSelectedPlantedAcre(plantedAcre));
    dispatch(setSavePlantedAcreSuccess(true));
  };

  const handleClose = () => {
    onClose();
    reset(defaultValues);
    setReplanting(false);
  };

  const handleDisabledStatus = (status) => {
    if (mode === MODE_TYPES.ADD) {
      return (
        status === PLANTING_STATUS.FAILED ||
        status === PLANTING_STATUS.HARVESTED
      );
    }
    if (mode === MODE_TYPES.EDIT) {
      if (isPlanned && status === PLANTING_STATUS.HARVESTED) return true;
      if (isActive && status === PLANTING_STATUS.PLANNED) return true;
    }
    return false;
  };

  const handleDisabledDates = (date) => {
    if (status === PLANTING_STATUS.PLANNED) {
      const today = new Date(new Date().toDateString());
      return date < today;
    } else if (
      status === PLANTING_STATUS.ACTIVE ||
      status === PLANTING_STATUS.FAILED ||
      status === PLANTING_STATUS.HARVESTED
    ) {
      return date > new Date();
    }
    return false;
  };

  const handleFailedAcresChange = (e) => {
    const selectedAcres = failedAcresList.find(
      (acres) => acres.id === Number(e.target.value)
    );
    if (selectedAcres) setValue("acres", selectedAcres.acres);
    else setValue("acres", "");
  };

  const handleDisabledInput = () => {
    return isSharedViewOnly || isFailed || isHarvested;
  };

  const showPlantedAcresNote = () => {
    if (failedCropId && mode === MODE_TYPES.EDIT) {
      if (isFailed) {
        return (
          <div className="failed-note-info">
            <small>
              {FAILED_FROM_NOTE} ({editedPlantedAcre.failedAcresName}) acres.
            </small>
          </div>
        );
      }
      return (
        <div className="replanting-note-info">
          <small>
            {REPLANTING_FOR_NOTE} ({editedPlantedAcre.failedAcresName}) failed
            acres.
          </small>
        </div>
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      fullscreen="sm-down"
      aria-labelledby="planted-acres-form-popup"
    >
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie
              options={PLANTED_ACRES_LOTTIE_OPTIONS}
              height={200}
              width={200}
            />
          }
        />
      )}
      <Modal.Header closeButton>
        <Modal.Title id="planted-acres-form-popup">
          {mode === MODE_TYPES.ADD
            ? ADD_PLANTED_ACRES
            : handleDisabledInput()
            ? VIEW_PLANTED_ACRES
            : EDIT_PLANTED_ACRES}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Modal.Body>
          {showPlantedAcresNote()}
          <Row>
            {mode === MODE_TYPES.ADD && (
              <Col className="mb-3" xs={6} md={4}>
                <Form.Group>
                  <Form.Label>{LABEL_REPLANTING_FAILED_ACRES}</Form.Label>
                  <br />
                  <>
                    <Form.Check
                      inline
                      type="radio"
                      label="No"
                      aria-label="No"
                      name="replanting"
                      checked={!replanting}
                      onChange={() => setReplanting(false)}
                      className="ms-1 mt-1"
                      disabled={handleDisabledInput()}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Yes"
                      aria-label="Yes"
                      name="replanting"
                      checked={replanting}
                      onChange={() => setReplanting(true)}
                      disabled={handleDisabledInput()}
                    />
                  </>
                </Form.Group>
              </Col>
            )}
            {replanting && (
              <Col className="mb-3" xs={6} md={8}>
                <Form.Group>
                  <Form.Label>{LABEL_SELECT_FAILED_ACRES}</Form.Label>
                  <Controller
                    name="failedCropId"
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        className="input-underline"
                        aria-label="Select Failed Acres"
                        disabled={handleDisabledInput()}
                        onChange={(e) => {
                          field.onChange(e);
                          handleFailedAcresChange(e);
                        }}
                      >
                        <option value="">{LABEL_SELECT_FAILED_ACRES}</option>
                        {failedAcresList.map((acres) => (
                          <option key={acres.id} value={acres.id}>
                            {acres.name} ({acres.acres} Acres)
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
          <Row>
            <Col className="mb-3" xs={8}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_PLANTED_ACRES_NAME,
                    validate: (value) => {
                      const plantedAcresExists = plantedAcres.some(
                        (acres) =>
                          acres.id !== editedPlantedAcre?.id &&
                          acres.name.toLowerCase() === value.toLowerCase()
                      );
                      return (
                        !plantedAcresExists || PLANTED_ACRES_NAME_ALREADY_EXISTS
                      );
                    },
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      className="input-underline"
                      isInvalid={!!errors.name}
                      placeholder={LABEL_ENTER_PLANTED_ACRES_NAME}
                      maxLength={50}
                      disabled={handleDisabledInput()}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col className="mb-3" xs={4}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Controller
                  name="status"
                  control={control}
                  rules={{ required: PLEASE_SELECT_STATUS }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="input-underline"
                      aria-label="Select Status"
                      isInvalid={!!errors.status}
                      disabled={handleDisabledInput()}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue("eventDate", null);
                      }}
                    >
                      {Object.values(PLANTING_STATUS).map((status) => (
                        <option
                          key={status}
                          value={status}
                          disabled={handleDisabledStatus(status)}
                        >
                          {status}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.status?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mb-3" xs={4}>
              <Form.Group>
                <Controller
                  name="eventDate"
                  control={control}
                  rules={{ required: PLEASE_SELECT_DATE }}
                  render={({ field }) => (
                    <DatePicker
                      label={`${status} Date`}
                      date={field.value}
                      onChange={field.onChange}
                      isInvalid={!!errors.eventDate}
                      handleDisabledDates={handleDisabledDates}
                      disabled={handleDisabledInput()}
                    />
                  )}
                />
                {errors.eventDate && (
                  <div className="custom-invalid-feedback mt-1">
                    {errors.eventDate.message}
                  </div>
                )}
              </Form.Group>
            </Col>
            {status === PLANTING_STATUS.FAILED && (
              <Col className="mb-3" xs={4}>
                <Form.Group controlId="formFallowAcres">
                  <Form.Label>Failed Acres</Form.Label>
                  <Controller
                    name="failedAcres"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_FAILED_ACRES,
                      validate: (value) =>
                        Number(value) <= Number(acres) ||
                        FAILED_ACRES_CANNOT_EXCEED_ACRES,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        className="input-underline"
                        type="text"
                        placeholder={LABEL_ENTER_FAILED_ACRES}
                        isInvalid={!!errors.failedAcres}
                        maxLength={10}
                        disabled={handleDisabledInput()}
                        onChange={(e) => {
                          InputHelper.validateFloatNumberInput(
                            e,
                            field.onChange
                          );
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.failedAcres?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
          </Row>
          <Row>
            <Col className="mb-3" xs={6} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Crop</Form.Label>
                <Controller
                  name="cropName"
                  control={control}
                  rules={{ required: PLEASE_SELECT_CROP }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="input-underline"
                      aria-label="Select Crop"
                      isInvalid={!!errors.cropName}
                      disabled={handleDisabledInput()}
                    >
                      <option value="">{LABEL_SELECT_CROP}</option>
                      {crops.map((cropName) => (
                        <option key={cropName} value={cropName}>
                          {cropName}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cropName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mb-3" xs={6} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Tillage</Form.Label>
                <Controller
                  name="tillageName"
                  control={control}
                  rules={{ required: PLEASE_SELECT_TILLAGE }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="input-underline"
                      aria-label="Select Tillage"
                      isInvalid={!!errors.tillageName}
                      disabled={handleDisabledInput()}
                    >
                      <option value="">{LABEL_SELECT_TILLAGE}</option>
                      {tillages.map((tillage) => (
                        <option key={tillage} value={tillage}>
                          {tillage}
                        </option>
                      ))}
                      <option value="N-Till">N-Till</option>
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tillageName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mb-3" xs={6} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Acres</Form.Label>
                <Controller
                  name="acres"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_ACRES,
                    validate: (value) =>
                      Number(value) > 0 || ACRES_MUST_BE_GREATER_THAN_ZERO,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      className="input-underline"
                      isInvalid={!!errors.acres}
                      placeholder={LABEL_ENTER_ACRES}
                      maxLength={10}
                      disabled={handleDisabledInput() || replanting}
                      onChange={(e) => {
                        InputHelper.validateFloatNumberInput(e, field.onChange);
                      }}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.acres?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col className="mb-3" xs={6} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Seed</Form.Label>
                <Controller
                  name="seedName"
                  control={control}
                  rules={{ required: PLEASE_SELECT_SEED }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="input-underline"
                      aria-label="Select Seed"
                      isInvalid={!!errors.seedName}
                      disabled={handleDisabledInput()}
                    >
                      <option value="">{LABEL_SELECT_SEED}</option>
                      {seeds.map((seed) => (
                        <option key={seed} value={seed}>
                          {seed}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.seedName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mb-3" xs={6} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Seeding Amount</Form.Label>
                <Controller
                  name="seedingAmount"
                  control={control}
                  rules={{ required: PLEASE_ENTER_SEEDING_AMOUNT }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      className="input-underline"
                      isInvalid={!!errors.seedingAmount}
                      placeholder={LABEL_ENTER_SEEDING_AMOUNT}
                      disabled={handleDisabledInput()}
                      onChange={(e) => {
                        const number = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        field.onChange(number);
                      }}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.seedingAmount?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mb-3" xs={8} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Seeding Rate</Form.Label>
                <br />
                <Controller
                  name="seedingRate"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Check
                        inline
                        type="radio"
                        label="Pounds"
                        aria-label="Pounds"
                        name="seedingRate"
                        checked={field.value === "pounds"}
                        onChange={() => field.onChange("pounds")}
                        className="ms-1 mt-1"
                        disabled={handleDisabledInput()}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Count"
                        aria-label="Count"
                        name="seedingRate"
                        checked={field.value === "count"}
                        onChange={() => field.onChange("count")}
                        disabled={handleDisabledInput()}
                      />
                    </>
                  )}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col className="mb-3" xs={6} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Row Spacing</Form.Label>
                <Controller
                  name="rowSpacing"
                  control={control}
                  rules={{ required: PLEASE_ENTER_ROW_SPACING }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      className="input-underline"
                      isInvalid={!!errors.rowSpacing}
                      placeholder={LABEL_ENTER_ROW_SPACING}
                      maxLength={10}
                      disabled={handleDisabledInput()}
                      onChange={(e) => {
                        const number = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        field.onChange(number);
                      }}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.rowSpacing?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mb-3" xs={6} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Pattern</Form.Label>
                <Controller
                  name="pattern"
                  control={control}
                  rules={{ required: PLEASE_SELECT_PATTERN }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      className="input-underline"
                      aria-label="Select Pattern"
                      isInvalid={!!errors.pattern}
                      disabled={handleDisabledInput()}
                    >
                      <option value="">{LABEL_SELECT_PATTERN}</option>
                      {PATTERNS.map((pattern) => (
                        <option key={pattern} value={pattern.toLowerCase()}>
                          {pattern}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.pattern?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        {!handleDisabledInput() && (
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {mode === MODE_TYPES.ADD ? "Add" : "Update"}
            </Button>
          </Modal.Footer>
        )}
      </Form>
    </Modal>
  );
};

export default PlantedAcresForm;
